import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { PaginationMeta, Tenant } from '@/lib/shared-types';
import { formatDateTime } from '@/lib/i18n';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

interface TenantRow extends Tenant {
  status: string;
  createdAt: string;
  _count?: { branches: number; users: number };
}

const STATUS_STYLES: Record<string, string> = {
  ACTIVE: 'bg-green-100 text-green-700',
  TRIAL: 'bg-blue-100 text-blue-700',
  SUSPENDED: 'bg-amber-100 text-amber-700',
  ARCHIVED: 'bg-slate-100 text-slate-600',
};

export default function Tenants() {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState<TenantRow[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(true);
      api
        .get<{ data: TenantRow[]; meta: PaginationMeta }>('/tenants', {
          params: { search: search || undefined, limit: 25 },
        })
        .then(({ data }) => {
          setRows(data.data ?? []);
          setMeta(data.meta ?? null);
        })
        .catch(() => toast.error('Could not load tenants'))
        .finally(() => setLoading(false));
    }, 250); // debounce the search box

    return () => clearTimeout(timer);
  }, [search]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-lg font-semibold tracking-tight">{t('nav.tenants')}</h1>
        <Input
          className="w-full sm:w-64"
          placeholder={t('common.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="scrollbar-thin overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="table-head border-b border-[var(--border)] bg-[var(--surface-sunken)] text-start text-[var(--muted-foreground)]">
                <tr>
                  <th className="p-3 text-start font-medium">Name</th>
                  <th className="p-3 text-start font-medium">Slug</th>
                  <th className="p-3 text-start font-medium">Status</th>
                  <th className="p-3 text-start font-medium">Branches</th>
                  <th className="p-3 text-start font-medium">Users</th>
                  <th className="p-3 text-start font-medium">Created</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-[var(--muted-foreground)]">
                      {t('common.loading')}
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-[var(--muted-foreground)]">
                      {t('common.empty')}
                    </td>
                  </tr>
                ) : (
                  rows.map((tenant) => (
                    <tr key={tenant.id} className="border-b border-[var(--border)] last:border-0 hover:bg-[var(--surface-hover)]">
                      <td className="p-3 font-medium">{tenant.name}</td>
                      <td className="p-3 text-[var(--muted-foreground)]">{tenant.slug}</td>
                      <td className="p-3">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs ${
                            STATUS_STYLES[tenant.status] ?? STATUS_STYLES.ARCHIVED
                          }`}
                        >
                          {tenant.status}
                        </span>
                      </td>
                      <td className="p-3">{tenant._count?.branches ?? '—'}</td>
                      <td className="p-3">{tenant._count?.users ?? '—'}</td>
                      <td className="p-3 text-[var(--muted-foreground)]">
                        {formatDateTime(tenant.createdAt, i18n.language)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {meta && (
        <p className="text-xs text-[var(--muted-foreground)]">
          {meta.total} tenants · page {meta.page} of {meta.pages}
        </p>
      )}
    </div>
  );
}
